import { useState } from "react";
import { crmBook, crmMates, saveBook, shareFiche } from "./api";
import { currentUserId } from "./auth";
import { Modal } from "./Modal";
import { OutsideShareField } from "./outside";
import { MultiChoiceField } from "./pickers";
import type { CrmFiche } from "./types";

export function ficheShareHint(fiche: CrmFiche): { label: string; people: { name: string; society: string }[]; tone: "in" | "out" } | null {
  if (!fiche.mine) {
    return {
      label: "Partagée par",
      people: [{ name: fiche.author_name.trim() || "un collègue", society: fiche.circle_name || "" }],
      tone: "in",
    };
  }
  const self = Number(currentUserId()) || 0;
  const people = fiche.shared_with
    .filter((id) => id > 0 && id !== self)
    .map((id) => {
      const mate = crmMates().find((row) => row.id === id);
      return { name: mate?.name || "", society: mate?.society || "" };
    })
    .filter((row) => row.name !== "");
  if (people.length === 0) return null;
  return { label: "Partagée avec", people, tone: "out" };
}

export function FicheShareHint({ fiche }: { fiche: CrmFiche }) {
  const hint = ficheShareHint(fiche);
  if (!hint) return null;
  const who = hint.tone === "out" ? "text-sky-700" : "text-violet-700";
  const society = hint.tone === "out" ? "text-sky-700" : "text-violet-700";
  return (
    <span className="block min-w-0">
      <span className="block truncate text-[11px] font-medium text-slate-500">{hint.label}</span>
      {hint.people.map((person) => (
        <span key={`${person.name}-${person.society}`} className="block min-w-0">
          <span className={`block truncate text-[11px] font-medium ${who}`}>{person.name}</span>
          {person.society ? <span className={`block truncate text-[11px] font-medium ${society}`}>{person.society}</span> : null}
        </span>
      ))}
    </span>
  );
}

export function FicheShareLine({ fiche, onChange }: { fiche: CrmFiche; onChange: (fiche: CrmFiche) => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [outsideIds, setOutsideIds] = useState<number[]>([]);
  if (!fiche.mine) {
    const hint = ficheShareHint(fiche);
    const person = hint?.people[0];
    return (
      <p className="text-xs font-semibold text-slate-500">
        <span className="block">{hint?.label || "Partagée par"}</span>
        {person ? <span className="block text-violet-700">{person.name}</span> : null}
        {person?.society ? <span className="block font-medium text-violet-700">{person.society}</span> : null}
      </p>
    );
  }
  const self = Number(currentUserId()) || 0;
  const options = crmMates().filter((mate) => mate.id !== self).map((mate) => ({ value: String(mate.id), label: mate.name }));

  async function apply(shared: number[], deskPrivate: boolean) {
    if (busy) return;
    setBusy(true);
    setNote("");
    try {
      const saved = await shareFiche(fiche.id, shared, deskPrivate);
      onChange(saved.fiche);
      if (saved.coffre) setNote(saved.coffre);
    } catch (reason: unknown) {
      setNote(reason instanceof Error ? reason.message : "Partage impossible");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-w-0 flex-col items-end gap-1">
      <div className="flex flex-nowrap items-end justify-end gap-2">
        <div className="w-36 shrink-0">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Partager</p>
          <MultiChoiceField
            values={fiche.shared_with.map(String)}
            options={options}
            onChange={(values) => void apply(values.map((value) => Number(value) || 0).filter((id) => id > 0), fiche.desk_private)}
          />
        </div>
        <OutsideShareField kind="fiche" ficheId={fiche.id} ids={outsideIds} onChange={setOutsideIds} persist compact />
        <button
          type="button"
          className={fiche.desk_private ? "crm-desk-out shrink-0 rounded-xl px-3 py-2 text-xs font-semibold" : "crm-desk-in shrink-0 rounded-xl px-3 py-2 text-xs font-semibold"}
          aria-pressed={fiche.desk_private}
          disabled={busy}
          title={fiche.desk_private ? "Cette fiche ne suit pas le partage du bureau" : "Cette fiche suit le partage du bureau"}
          onClick={() => void apply(fiche.shared_with, !fiche.desk_private)}
        >
          {fiche.desk_private ? "Hors du bureau" : "Dans le bureau"}
        </button>
      </div>
      {note ? <p className="text-xs text-rose-700">{note}</p> : null}
    </div>
  );
}

export function BookShareButton({ compact = false }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const [ids, setIds] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const self = Number(currentUserId()) || 0;
  const options = crmMates().filter((mate) => mate.id !== self).map((mate) => ({ value: String(mate.id), label: mate.name }));

  function show() {
    setIds(crmBook().map(String));
    setNote("");
    setOpen(true);
  }

  async function apply(values: string[]) {
    setIds(values);
    setBusy(true);
    setNote("");
    try {
      const coffre = await saveBook(values.map((value) => Number(value) || 0).filter((id) => id > 0));
      if (coffre) setNote(coffre);
    } catch (reason: unknown) {
      setNote(reason instanceof Error ? reason.message : "Partage du bureau impossible");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className="crm-pill rounded-full px-3 py-1.5 text-xs font-semibold" onClick={show}>{compact ? "Bureau" : "Partager le bureau"}</button>
      {open ? (
        <Modal onClose={() => setOpen(false)} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
          {() => (
            <>
              <h2 className="text-lg font-semibold">Partager le bureau</h2>
              <p className="mt-1 text-sm text-slate-500">Ces collègues voient toutes tes fiches, y compris les prochaines. Une fiche « Hors du bureau » reste chez toi. Le Coffre ne partage que le dossier de chaque fiche.</p>
              <div className="mt-4">
                <MultiChoiceField values={ids} options={options} onChange={(values) => void apply(values)} />
              </div>
              {note ? <p className="mt-3 text-sm text-rose-700">{note}</p> : null}
              {busy ? <p className="mt-3 text-xs text-slate-500">Enregistrement…</p> : null}
            </>
          )}
        </Modal>
      ) : null}
    </>
  );
}
